import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Typography } from "../components/utilities/Typhograpy";
import GlobalContext from "../context/GlobalContext";
import bannerImage from "../assets/images/image/banner_image.png";
import QuickActionButtons from "../components/button/QuickActionButtons";
import EssentialSetup from "../components/dashboard/EssentialSetup";
import "../assets/css/dashboard.css";
import { Helmet } from "react-helmet-async";
import { Badge, Button, Modal, ProgressBar, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { getSettings } from "../api/CompanyApi";
import { fetchTemplatePayroll } from "../api/PayrollApi";
import { fetchLocations } from "../api/LocationsApi";
import { fetchHolidays } from "../api/HolidayApi";
import { fetchSchedules } from "../api/ScheduleApi";
import { getUsers } from "../api/UsersApi";
import { useTranslation } from "react-i18next";

const getOnboardingStorageKey = (companyId) => `onboardingWizard:${companyId}`;

// Judul, deskripsi & label tombol tiap langkah ada di i18n/locales/<bahasa>/dashboard.json (steps.<id>).
// `review: true` = template dibuat otomatis saat registrasi dari preset negara; langkah ini
// meminta admin meninjaunya (selesai setelah dibuka dari checklist atau admin membuat template sendiri).
// Teks versi tinjau ada di steps.<id>_review.
const ONBOARDING_STEPS = [
  { id: "company_profile", target: "/settings" },
  { id: "company_logo", target: "/settings" },
  { id: "payroll_logo", target: "/settings" },
  { id: "tax_id", target: "/settings" },
  { id: "website_linkedin", target: "/settings" },
  { id: "payroll_schema", target: "/payroll/form", review: true, reviewTarget: "/payroll?tab=template" },
  { id: "attendance_location", target: "/attendance?tab=locations" },
  { id: "holiday_schedule", target: "/attendance?tab=holiday" },
  { id: "schedule_template", target: "/attendance?tab=schedules", review: true },
  { id: "invite_employee", target: "/users/new" },
];

const DashboardPage = () => {
  const { user } = useContext(GlobalContext);
  const navigate = useNavigate();
  const { t } = useTranslation("dashboard");
  const companyId = user?.workspace?.company_id;
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoadingOnboarding, setIsLoadingOnboarding] = useState(false);
  const [stepStatuses, setStepStatuses] = useState({});
  // Langkah `review` yang templatenya sudah ada (dibuat otomatis) — tampil sebagai "Tinjau …".
  const [reviewStepIds, setReviewStepIds] = useState([]);
  const stepText = (step, field) =>
    t(`steps.${step.id}${reviewStepIds.includes(step.id) ? "_review" : ""}.${field}`);
  const onboardingRequestRef = useRef(0);

  const persistStatuses = useCallback(
    (nextStatuses) => {
      if (!companyId) return;
      localStorage.setItem(
        getOnboardingStorageKey(companyId),
        JSON.stringify(nextStatuses),
      );
    },
    [companyId],
  );

  const readPersistedStatuses = useCallback(() => {
    if (!companyId) return {};

    try {
      const raw = localStorage.getItem(getOnboardingStorageKey(companyId));
      if (!raw) return {};
      return JSON.parse(raw);
    } catch (error) {
      return {};
    }
  }, [companyId]);

  const getDataDrivenCompletion = useCallback(async () => {
    if (!companyId) return {};

    const [
      settingsRes,
      payrollRes,
      locationsRes,
      holidaysRes,
      schedulesRes,
      usersRes,
    ] = await Promise.all([
      getSettings(companyId),
      fetchTemplatePayroll({ companyId, page: 1, perPage: 1 }),
      fetchLocations({ companyId, page: 1, perPage: 1 }),
      fetchHolidays({ companyId, page: 1, perPage: 1 }),
      fetchSchedules({ companyId, page: 1, perPage: 1 }),
      getUsers({ companyId, page: 1, perPage: 1, search: "", status: "all" }),
    ]);

    const settings = settingsRes?.data || {};
    const getTotal = (res) => Number(res?.total || 0);

    return {
      company_profile:
        Boolean(settings.company_address) && Boolean(settings.company_phone),
      company_logo: Boolean(settings.company_logo),
      payroll_logo: Boolean(settings.payroll_logo),
      tax_id: Boolean(settings.company_tax_id),
      website_linkedin:
        Boolean(settings.company_website) && Boolean(settings.company_linkedin),
      payroll_schema: getTotal(payrollRes) > 0,
      attendance_location: getTotal(locationsRes) > 0,
      holiday_schedule: getTotal(holidaysRes) > 0,
      schedule_template: getTotal(schedulesRes) > 0,
      invite_employee: getTotal(usersRes) > 1,
      // Lebih dari satu template = admin sudah membuat template sendiri (tidak perlu ditinjau lagi).
      payroll_schema_custom: getTotal(payrollRes) > 1,
      schedule_template_custom: getTotal(schedulesRes) > 1,
    };
  }, [companyId]);

  const refreshOnboardingStatus = useCallback(async () => {
    if (!companyId) {
      setStepStatuses({});
      setShowOnboarding(false);
      return;
    }

    const requestId = onboardingRequestRef.current + 1;
    onboardingRequestRef.current = requestId;

    setIsLoadingOnboarding(true);
    setStepStatuses({});
    try {
      const persisted = readPersistedStatuses();
      const completedByData = await getDataDrivenCompletion();

      if (onboardingRequestRef.current !== requestId) {
        return;
      }

      const needsReview = ONBOARDING_STEPS.filter(
        (step) =>
          step.review &&
          completedByData[step.id] &&
          !completedByData[`${step.id}_custom`] &&
          !["reviewed", "completed"].includes(persisted[step.id]),
      ).map((step) => step.id);
      setReviewStepIds(
        ONBOARDING_STEPS.filter((step) => step.review && completedByData[step.id]).map(
          (step) => step.id,
        ),
      );

      const mergedStatuses = ONBOARDING_STEPS.reduce((acc, step) => {
        if (completedByData[step.id] && !needsReview.includes(step.id)) {
          acc[step.id] = "completed";
        } else if (persisted[step.id] === "skipped") {
          acc[step.id] = "skipped";
        } else {
          acc[step.id] = "pending";
        }
        return acc;
      }, {});

      setStepStatuses(mergedStatuses);
      persistStatuses(mergedStatuses);

      const hasSeenWizardKey = `${getOnboardingStorageKey(companyId)}:seen`;
      const hasSeenWizard = localStorage.getItem(hasSeenWizardKey) === "true";
      const hasPending = Object.values(mergedStatuses).some(
        (s) => s !== "completed",
      );

      if (!hasSeenWizard && hasPending) {
        localStorage.setItem(hasSeenWizardKey, "true");
        setShowOnboarding(true);
      }
    } finally {
      if (onboardingRequestRef.current === requestId) {
        setIsLoadingOnboarding(false);
      }
    }
  }, [
    companyId,
    getDataDrivenCompletion,
    persistStatuses,
    readPersistedStatuses,
  ]);

  useEffect(() => {
    refreshOnboardingStatus();
  }, [refreshOnboardingStatus]);

  useEffect(() => {
    setShowOnboarding(false);
    setStepStatuses({});
  }, [companyId]);

  const completedCount = useMemo(
    () =>
      Object.values(stepStatuses).filter((status) => status === "completed")
        .length,
    [stepStatuses],
  );

  const progressPercent = useMemo(
    () => Math.round((completedCount / ONBOARDING_STEPS.length) * 100),
    [completedCount],
  );

  const isOnboardingComplete = useMemo(
    () =>
      ONBOARDING_STEPS.length > 0 && completedCount === ONBOARDING_STEPS.length,
    [completedCount],
  );

  const currentStep = useMemo(
    () =>
      ONBOARDING_STEPS.find((step) => stepStatuses[step.id] === "pending") ||
      null,
    [stepStatuses],
  );

  const handleSkipStepById = (stepId) => {
    if (!stepId) return;

    const nextStatuses = {
      ...stepStatuses,
      [stepId]: "skipped",
    };

    setStepStatuses(nextStatuses);
    persistStatuses(nextStatuses);
  };

  const handleOpenStepAction = (step) => {
    if (!step) return;

    // Membuka template otomatis dari checklist = sudah ditinjau.
    const isReview = step.review && reviewStepIds.includes(step.id);
    if (isReview) {
      const nextStatuses = { ...stepStatuses, [step.id]: "reviewed" };
      setStepStatuses(nextStatuses);
      persistStatuses(nextStatuses);
    }

    setShowOnboarding(false);
    navigate(isReview && step.reviewTarget ? step.reviewTarget : step.target);
  };

  const getStepBadge = (status) => {
    if (status === "completed") return <Badge bg="success">{t("status.completed")}</Badge>;
    if (status === "skipped") return <Badge bg="secondary">{t("status.skipped")}</Badge>;
    return (
      <Badge bg="warning" text="dark">
        {t("status.pending")}
      </Badge>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t("meta_title")}</title>
      </Helmet>
      <div className="row">
        <div className="col-12">
          <div className="banner-dashboard">
            <div className="banner-dashboard__content">
              <Typography variant="h5" style={{ fontWeight: 600 }}>
                {t("welcome", { name: user.first_name })}
              </Typography>
              <Typography variant="body1">
                {t("greeting")}
              </Typography>
              <EssentialSetup
                companyId={companyId}
                companyName={user?.workspace?.company_name}
              />
            </div>
            <img src={bannerImage}></img>
          </div>
        </div>
        <div className="col-12 mt-3">
          <div className="quick-action">
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {t("quick_actions.title")} &#9889;
            </Typography>
            <div className="mt-3">
              <QuickActionButtons />
            </div>
          </div>
        </div>

        <div className="col-12 mt-3">
          <div
            className={`quick-action onboarding-progress-card ${isOnboardingComplete ? "completed" : ""}`}
          >
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 p-3">
              <div>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  {t("setup.title")}
                </Typography>
                <Typography variant="body1">
                  {isOnboardingComplete
                    ? t("setup.complete_text")
                    : t("setup.progress", { done: completedCount, total: ONBOARDING_STEPS.length })}
                </Typography>
              </div>
              <Button
                variant={isOnboardingComplete ? "light" : "outline-primary"}
                onClick={() => setShowOnboarding(true)}
                disabled={isLoadingOnboarding}
              >
                {isLoadingOnboarding
                  ? t("setup.checking")
                  : isOnboardingComplete
                    ? t("setup.view_checklist")
                    : t("setup.continue")}
              </Button>
            </div>
            {isOnboardingComplete ? (
              <div className="onboarding-complete-state mt-3">
                <div className="onboarding-complete-copy-block">
                  <div className="onboarding-complete-pill">
                    <CheckCircle2 size={14} strokeWidth={2.4} />
                    <span>{t("setup.checklist_complete")}</span>
                  </div>
                  <div className="onboarding-complete-title">
                    {t("setup.ready_title")}
                  </div>
                  <div className="onboarding-complete-copy">
                    {t("setup.ready_copy")}
                  </div>
                  <div className="onboarding-complete-meta">
                    <div className="onboarding-complete-meta-chip strong">
                      {t("setup.items_done", { count: completedCount })}
                    </div>
                    <div className="onboarding-complete-meta-chip">
                      {t("setup.no_pending")}
                    </div>
                  </div>
                </div>
                <div className="onboarding-complete-emblem" aria-hidden="true">
                  <div className="onboarding-complete-emblem-ring">
                    <CheckCircle2 size={28} strokeWidth={2.4} />
                  </div>
                  <div className="onboarding-complete-emblem-label">{t("setup.ready_label")}</div>
                </div>
              </div>
            ) : (
              <div className="mt-3">
                <ProgressBar
                  now={progressPercent}
                  label={`${progressPercent}%`}
                  className="onboarding-progress-bar"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        show={showOnboarding}
        onHide={() => setShowOnboarding(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("modal.title")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <div>
              <strong>
                {t("modal.progress", { done: completedCount, total: ONBOARDING_STEPS.length })}
              </strong>
            </div>
            {isLoadingOnboarding && (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner size="sm" animation="border" />
                {t("modal.refreshing")}
              </div>
            )}
          </div>

          {!isOnboardingComplete && (
            <ProgressBar
              now={progressPercent}
              className="mb-3 onboarding-progress-bar"
            />
          )}

          {currentStep ? (
            <div className="onboarding-current-step mb-3">
              <h6 className="mb-1">{t("modal.current_step", { title: stepText(currentStep, "title") })}</h6>
              <p className="mb-0 text-muted">{stepText(currentStep, "description")}</p>
            </div>
          ) : (
            <div className="onboarding-current-step onboarding-current-step-complete mb-3">
              <div className="onboarding-complete-pill mb-2">
                <CheckCircle2 size={14} strokeWidth={2.4} />
                <span>{t("modal.workspace_ready")}</span>
              </div>
              <h6 className="mb-1">{t("modal.all_completed")}</h6>
              <p className="mb-0 text-muted">
                {t("modal.all_completed_text")}
              </p>
            </div>
          )}

          <div className="onboarding-steps-list">
            {ONBOARDING_STEPS.map((step, index) => (
              <div key={step.id} className="onboarding-step-item">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div>
                    <div className="fw-semibold">
                      {index + 1}. {stepText(step, "title")}
                    </div>
                    <div className="text-muted small">{stepText(step, "description")}</div>
                  </div>
                  {getStepBadge(stepStatuses[step.id] || "pending")}
                </div>
                {(stepStatuses[step.id] || "pending") !== "completed" && (
                  <div className="d-flex gap-2 mt-2">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => handleOpenStepAction(step)}
                    >
                      {stepText(step, "action")}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => handleSkipStepById(step.id)}
                    >
                      {t("modal.skip")}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button
            variant="outline-secondary"
            onClick={() => setShowOnboarding(false)}
          >
            {t("modal.close")}
          </Button>
          <div className="d-flex gap-2">
            {currentStep && (
              <Button
                variant="outline-dark"
                onClick={() => handleSkipStepById(currentStep.id)}
              >
                {t("modal.skip_for_now")}
              </Button>
            )}
            {currentStep && (
              <Button
                variant="primary"
                onClick={() => handleOpenStepAction(currentStep)}
              >
                {stepText(currentStep, "action")}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DashboardPage;
